import type { ReactNode } from "react"
import { useState } from "react"
import { Link } from "@tanstack/react-router"
import {
	IconCode2Outline18 as ApiCode,
	IconAtom2Outline18 as Atom,
	IconBookOpen4Outline18 as BookOpen,
	IconBugOutline18 as Bug,
	IconCalendarClockOutline18 as CalendarClock,
	IconChartBarOutline18 as ChartBar,
	IconChevronRightOutline18 as ChevronRight,
	IconCodeBranchOutline18 as CodeBranch,
	IconCopyrightOutline18 as Copyright,
	IconCubeOutline18 as Cube,
	IconDarkLightOutline18 as DarkLight,
	IconFingerprintOutline18 as Fingerprint,
	IconLanguageOutline18 as Language,
	IconLifeRingOutline18 as LifeRing,
	IconLinkOutline18 as LinkIcon,
	IconOrbitSparkleOutline18 as OrbitSparkle,
	IconPenWritingOutline18 as PenWriting,
	IconSquareArrowUpRightOutline18 as SquareArrowUpRight,
} from "nucleo-ui-outline-18"

import { cn } from "@productlane/lib/src/cn"
import {
	Badge,
	Button,
	Input,
	Select,
	SlackIconGray,
	Switch,
} from "@productlane/ui"
import { SortIcon } from "@productlane/ui/src/icons/app-nav-icons"
import { EnvelopeDuotoneIcon } from "@productlane/ui/src/icons/project/envelope-duotone-icon"
import { GitHubIcon } from "@productlane/ui/src/icons/project/github-icon"
import { MessagesDuotoneIcon } from "@productlane/ui/src/icons/project/messages-duotone-icon"

import type { SettingsNavRowIcon } from "../../settings/settings-pages/settings-nav-row"
import { CollapsiblePromptTextarea } from "@/components/collapsible-prompt-textarea"
import { isDevEnvironment } from "@/env"
import { NotFound } from "@/screens/not-found"
import { SettingsHeader } from "../../settings/settings-header"
import {
	settingsNavGroupClass,
	SettingsNavRow,
} from "../../settings/settings-pages/settings-nav-row"
import { SettingItem } from "../../settings/settings-switches/setting-item"
import {
	SETTINGS_CARD_CLASS,
	SettingsCardInsetContext,
	SettingsContainer,
} from "../../settings/settings-switches/settings-container"
import { SettingsSection } from "../../settings/settings-switches/settings-section"
import { DesignIssueHeader } from "./design-issue-header"

type IconRule = "A" | "B" | "C"
type RowIcon = Parameters<typeof SettingItem>[0]["Icon"]

const VERSIONS: { rule: IconRule; line: string }[] = [
	{ rule: "A", line: "Icons on every row, switches and selects included." },
	{
		rule: "B",
		line: "No icons anywhere, like Linear. Integration logos stay.",
	},
	{
		rule: "C",
		line: "Icons only on rows that open another page. Claude recommends this one.",
	},
]

const FREQUENCY_OPTIONS = [
	{ value: "daily", label: "Daily" },
	{ value: "weekly", label: "Weekly" },
	{ value: "monthly", label: "Monthly" },
]

const WRITING_PROMPT = `# Writing help center articles (Productlane)

## Voice
Write like a person on the support team explaining the feature to a customer. Plain present tense, active voice, second person ("you").`

function Row({
	rule,
	Icon,
	logo,
	title,
	description,
	action,
	children,
}: {
	rule: IconRule
	Icon: RowIcon
	logo?: boolean
	title: ReactNode
	description: ReactNode
	action?: ReactNode
	children?: ReactNode
}) {
	// Rows with a control keep their icon only in version A; integration logos always stay.
	const showIcon = logo || rule === "A"
	return (
		<div className="flex flex-col gap-3 pt-3 first:pt-0">
			<SettingItem
				Icon={showIcon ? Icon : undefined}
				title={title}
				description={description}
				action={action}
			/>
			{children}
		</div>
	)
}

function ToggleRow(props: {
	rule: IconRule
	Icon: RowIcon
	logo?: boolean
	title: string
	description: string
	defaultChecked?: boolean
	children?: ReactNode
}) {
	const [checked, setChecked] = useState(props.defaultChecked ?? true)
	return (
		<Row
			{...props}
			action={
				<Switch
					label={null}
					name={props.title}
					checked={checked}
					onCheckedChange={setChecked}
				/>
			}
		/>
	)
}

// SettingsNavRow always draws the icon tile, so version B uses this copy without it.
function PlainNavRow({
	title,
	description,
	link,
}: {
	title: string
	description: string
	link: string
}) {
	return (
		<Link
			to={link}
			className="flex items-center justify-between gap-4 px-4 py-3.5 hover:bg-gray-50 dark:hover:bg-gray-800/50"
		>
			<div className="flex flex-col gap-0.5">
				<span className="text-sm font-medium text-primary">{title}</span>
				<span className="text-xs text-secondary">{description}</span>
			</div>
			<ChevronRight className="h-3.5 w-3.5 flex-none text-tertiary" />
		</Link>
	)
}

function NavRow({
	rule,
	icon,
	title,
	description,
}: {
	rule: IconRule
	icon: SettingsNavRowIcon
	title: string
	description: string
}) {
	const link = "/design/settings-standard"
	if (rule === "B")
		return <PlainNavRow title={title} description={description} link={link} />
	return (
		<SettingsNavRow
			icon={icon}
			title={title}
			description={description}
			link={link}
		/>
	)
}

function PublishCard({
	title,
	description,
	url,
	checked,
	onCheckedChange,
}: {
	title: string
	description: string
	url: string
	checked: boolean
	onCheckedChange: (value: boolean) => void
}) {
	return (
		<div className={cn(SETTINGS_CARD_CLASS, "-mx-3.5 p-4")}>
			<div className="flex select-none items-center justify-between gap-4">
				<div className="flex flex-col gap-0.5">
					<div className="text-sm font-medium">{title}</div>
					<div className="text-xs tracking-tight text-secondary">
						{description}
					</div>
				</div>
				<Switch
					label={null}
					name="public"
					checked={checked}
					onCheckedChange={onCheckedChange}
				/>
			</div>
			<Input
				readOnly
				name="url"
				value={url}
				className="mt-4 w-full"
				icon={SquareArrowUpRight}
				iconPosition="right"
			/>
		</div>
	)
}

function Group({
	title,
	dimmed,
	children,
}: {
	title: string
	dimmed?: boolean
	children: ReactNode
}) {
	return (
		<div className={cn(dimmed && "opacity-50")}>
			<SettingsSection title={title} className="mb-3 mt-0" />
			<SettingsContainer>{children}</SettingsContainer>
		</div>
	)
}

function NavGroup({ title, children }: { title: string; children: ReactNode }) {
	return (
		<div>
			<SettingsSection title={title} className="mb-3 mt-0" />
			<div className={settingsNavGroupClass}>{children}</div>
		</div>
	)
}

function PortalMock({ rule }: { rule: IconRule }) {
	const [analyticsOn, setAnalyticsOn] = useState(true)
	return (
		<>
			<SettingsHeader
				title="Portal"
				subtitle="Manage general settings for your docs, changelog and requests portal."
			/>
			<NavGroup title="Setup">
				<NavRow
					rule={rule}
					icon={DarkLight}
					title="Theme"
					description="Adjust the cover image and colors of your portal."
				/>
				<NavRow
					rule={rule}
					icon={LinkIcon}
					title="Custom domain"
					description="Connect your own domain to your portal."
				/>
				<NavRow
					rule={rule}
					icon={Fingerprint}
					title="SSO"
					description="Log users in to the portal automatically."
				/>
				<NavRow
					rule={rule}
					icon={Language}
					title="Language"
					description="Offer your portal and widget in more languages."
				/>
			</NavGroup>
			<Group title="Tracking and branding">
				<Row
					rule={rule}
					Icon={ChartBar}
					title="Google Analytics"
					description="Enable Google Analytics for your portal."
					action={
						<Switch
							label={null}
							name="google"
							checked={analyticsOn}
							onCheckedChange={setAnalyticsOn}
						/>
					}
				>
					{analyticsOn && (
						<Input name="googleTagId" placeholder="G-XXXXXXXXX" />
					)}
				</Row>
				<ToggleRow
					rule={rule}
					Icon={Copyright}
					title="Branding"
					description="Show Productlane branding on the portal."
				/>
			</Group>
		</>
	)
}

function DocsMock({ rule }: { rule: IconRule }) {
	const [isPublic, setIsPublic] = useState(true)
	const [frequency, setFrequency] = useState("weekly")
	return (
		<>
			<SettingsHeader
				title="Docs"
				subtitle="Publish beautiful, easy to use and maintainable docs."
				docsArticle="711a725d-dc34-4d34-8609-255364f5a33b"
			/>
			<PublishCard
				title="Docs are public"
				description="Make your docs available on a public domain."
				url="productlane.com/docs"
				checked={isPublic}
				onCheckedChange={setIsPublic}
			/>
			<Group title="Automatic drafts" dimmed={!isPublic}>
				<ToggleRow
					rule={rule}
					Icon={BookOpen}
					title="Self-updating docs"
					description="Draft articles from merged PRs and Linear issues."
				/>
				<Row
					rule={rule}
					Icon={GitHubIcon}
					logo
					title="GitHub"
					description="Needed to draft from merged PRs."
					action={<Button className="flex-none">Connect</Button>}
				/>
				<Row
					rule={rule}
					Icon={CalendarClock}
					title="Draft frequency"
					description="One set of drafts for everything that shipped."
					action={
						<Select
							name="frequency"
							value={frequency}
							options={FREQUENCY_OPTIONS}
							onChange={setFrequency}
							className="w-44"
						/>
					}
				/>
				<Row
					rule={rule}
					Icon={PenWriting}
					title="Writing instructions"
					description="Tell the AI how to write your docs."
				>
					<CollapsiblePromptTextarea
						name="docsCustomPrompt"
						aria-label="Writing instructions"
						defaultValue={WRITING_PROMPT}
						minRows={3}
					/>
				</Row>
			</Group>
			<Group title="Reader AI" dimmed={!isPublic}>
				<ToggleRow
					rule={rule}
					Icon={Atom}
					title="AI chat"
					description="Let readers ask questions about your docs."
				/>
				<ToggleRow
					rule={rule}
					Icon={OrbitSparkle}
					title="AI shortcuts"
					description="Show Open in ChatGPT and Open in Claude."
				/>
			</Group>
			<NavGroup title="Related">
				<NavRow
					rule={rule}
					icon={SortIcon}
					title="Portal settings"
					description="Set up SSO, custom domain, theme and a private portal."
				/>
			</NavGroup>
		</>
	)
}

function AgentMock({ rule }: { rule: IconRule }) {
	const [enabled, setEnabled] = useState(true)
	return (
		<>
			<SettingsHeader
				title="Productlane Agent"
				subtitle="Resolve customer questions with answers grounded in your docs."
			/>
			<SettingsContainer>
				<SettingItem
					Icon={rule === "A" ? Atom : undefined}
					title="Productlane Agent"
					description="Activate the agent for your widget, live chat and docs."
					action={
						<Switch
							label={null}
							name="agent"
							checked={enabled}
							onCheckedChange={setEnabled}
						/>
					}
				/>
			</SettingsContainer>
			<Group title="Deploy" dimmed={!enabled}>
				<ToggleRow
					rule={rule}
					Icon={MessagesDuotoneIcon}
					title="Live chat"
					description="Answers customers in the widget chat."
				/>
				<ToggleRow
					rule={rule}
					Icon={EnvelopeDuotoneIcon}
					title="Email"
					description="Answers inbound email and widget feedback."
				/>
				<ToggleRow
					rule={rule}
					Icon={SlackIconGray}
					logo
					title="Slack"
					description="Answers in the customer's Slack Connect thread."
				/>
				<ToggleRow
					rule={rule}
					Icon={ApiCode}
					title="API"
					description="Answers threads created through the public API."
					defaultChecked={false}
				/>
				<ToggleRow
					rule={rule}
					Icon={LifeRing}
					title="Support portal"
					description="Answers messages sent from the support portal."
				/>
				<ToggleRow
					rule={rule}
					Icon={Cube}
					title="Feature requests"
					description="Answers requests submitted in the portal."
					defaultChecked={false}
				/>
			</Group>
			<Group title="Linear" dimmed={!enabled}>
				<ToggleRow
					rule={rule}
					Icon={CodeBranch}
					title="Create Linear issues from agent chats"
					description="Files bugs and feature requests to Linear."
				/>
				<ToggleRow
					rule={rule}
					Icon={Bug}
					title="Auto-assign the agent to bugs"
					description="The agent picks up new bugs from AI chats."
					defaultChecked={false}
				/>
			</Group>
		</>
	)
}

const RULES: { title: string; body: string }[] = [
	{
		title: "Header",
		body: "Title, one-line subtitle, How it works link. SettingsHeader, as today.",
	},
	{
		title: "Main switch first",
		body: "The switch that turns the page on (public, agent on) sits alone in the first card, with no label above it. When it is off, every group below dims.",
	},
	{
		title: "Every group has a label",
		body: "Rows sit in one card per group, with a SettingsSection label above it. Each card carries a label, a single row included.",
	},
	{
		title: "One row shape",
		body: "Title, one-line description, control on the right. Selects are w-44. Buttons are outline or default, never red. Icons follow the version you pick below.",
	},
	{
		title: "Links come last",
		body: "Links to other settings pages go in a Related group at the bottom, as SettingsNavRow. Setting groups stay free of them.",
	},
	{
		title: "One line of description",
		body: "40 to 50 characters. The longer story goes in the How it works article.",
	},
]

const PAGES: { page: string; today: string; change: string }[] = [
	{
		page: "Docs",
		today: "No row icons. Portal settings is a loose card at the end.",
		change: "Apply the icon rule. Move Portal settings into Related.",
	},
	{
		page: "Feature requests",
		today:
			"No group labels. Portal settings and Multiple portals sit between setting cards. Team requests has a card to itself.",
		change: "Add Display and Access labels. Move both links into Related.",
	},
	{
		page: "Productlane Agent",
		today: "Group descriptions run two lines. Several row descriptions wrap.",
		change: "Drop group descriptions. Cut row descriptions to one line.",
	},
	{
		page: "General, Profile",
		today: "No row icons.",
		change: "Apply the icon rule.",
	},
	{
		page: "Linear integration",
		today: "7 rows, no icons, no group labels.",
		change: "Apply the icon rule. Add group labels.",
	},
	{
		page: "Notifications (email, Slack, inbox)",
		today: "21 rows; one icon on Slack, none on email or inbox.",
		change: "Apply the icon rule. Group by event type.",
	},
	{
		page: "SLA, API, Portal overrides, New portal",
		today: "No row icons.",
		change: "Apply the icon rule.",
	},
	{
		page: "Office hours",
		today: "Icons on some rows only.",
		change: "Apply the icon rule.",
	},
	{
		page: "Slack, GitHub, Cursor, Intercom",
		today: "Icons, but no group labels on pages with several groups.",
		change: "Apply the icon rule. Add group labels.",
	},
	{
		page: "Portal, Agent hub, Notifications hub",
		today: "Already SettingsNavRow lists.",
		change: "Icons stay in A and C, go in B.",
	},
]

const SCREENS: {
	label: string
	Mock: typeof PortalMock
}[] = [
	{
		label: "Settings / Portal (overview with navigation rows)",
		Mock: PortalMock,
	},
	{ label: "Settings / Docs (switches, select, Related link)", Mock: DocsMock },
	{ label: "Settings / Productlane Agent (all switches)", Mock: AgentMock },
]

function VersionFrame({
	rule,
	line,
	children,
}: {
	rule: IconRule
	line: string
	children: ReactNode
}) {
	return (
		<div className="flex-none space-y-3">
			<div className="space-y-0.5">
				<div className="text-sm font-semibold text-primary">Version {rule}</div>
				<div className="text-xs text-secondary">{line}</div>
			</div>
			<div className="rounded-xl bg-gray-50 px-8 py-10 ring-1 ring-black/5 dark:bg-gray-900 dark:ring-white/10">
				<div className="w-[704px] space-y-8">{children}</div>
			</div>
		</div>
	)
}

export function SettingsStandardPage() {
	if (!isDevEnvironment) return <NotFound />
	return (
		<SettingsCardInsetContext.Provider value={true}>
			<div className="min-h-screen space-y-12 bg-white px-10 py-10 pb-24 dark:bg-gray-950">
				<div className="max-w-4xl space-y-12">
					<DesignIssueHeader
						identifier="PRO-10799"
						title="Standardize settings pages and resolve inconsistent designs"
						url="https://linear.app/productlane/issue/PRO-10799"
						summary="Settings pages mix an older layout with a newer one, so Docs, Feature requests and the Agent page each look different. This design puts every page on one layout, with group labels and a Related section for links. Versions A, B and C compare three rules for row icons on the same pages."
					/>

					<div className="space-y-4">
						<div className="text-xs font-medium text-tertiary">
							The standard
						</div>
						<div
							className={cn(
								SETTINGS_CARD_CLASS,
								"divide-y divide-gray-200 dark:divide-gray-700/80",
							)}
						>
							{RULES.map((rule, index) => (
								<div key={rule.title} className="flex gap-4 px-4 py-3">
									<Badge variant="default" className="h-5 flex-none">
										{index + 1}
									</Badge>
									<div className="flex flex-col gap-0.5">
										<div className="text-sm font-medium">{rule.title}</div>
										<div className="text-xs text-secondary">{rule.body}</div>
									</div>
								</div>
							))}
						</div>
					</div>
				</div>

				{SCREENS.map(({ label, Mock }) => (
					<div key={label} className="space-y-4">
						<div className="text-xs font-medium text-tertiary">{label}</div>
						<div className="flex flex-nowrap items-start gap-10 overflow-x-auto pb-2">
							{VERSIONS.map(({ rule, line }) => (
								<VersionFrame key={rule} rule={rule} line={line}>
									<Mock rule={rule} />
								</VersionFrame>
							))}
						</div>
					</div>
				))}

				<div className="max-w-4xl space-y-4">
					<div className="text-xs font-medium text-tertiary">
						What each page needs
					</div>
					<div className={cn(SETTINGS_CARD_CLASS, "overflow-hidden")}>
						<div className="grid grid-cols-[180px_1fr_1fr] gap-4 border-b border-gray-200 px-4 py-2 text-xs font-medium text-tertiary dark:border-gray-700/80">
							<div>Page</div>
							<div>Today</div>
							<div>Change</div>
						</div>
						<div className="divide-y divide-gray-200 dark:divide-gray-700/80">
							{PAGES.map((row) => (
								<div
									key={row.page}
									className="grid grid-cols-[180px_1fr_1fr] gap-4 px-4 py-3 text-xs"
								>
									<div className="font-medium text-primary">{row.page}</div>
									<div className="text-secondary">{row.today}</div>
									<div className="text-primary">{row.change}</div>
								</div>
							))}
						</div>
					</div>
				</div>
			</div>
		</SettingsCardInsetContext.Provider>
	)
}
